import { withRequestDeadline, throwIfAborted } from './requestDeadline'
import { localSaveFailed, localSaveSucceeded } from '../pages/localSaveState'
/**
 * encryptedYjsProvider.ts — Client-side Encrypted Yjs WebSocket Provider for Superelectric Ink.
 *
 * Connects a local Y.Doc to the per-document Durable Object relay over WebSocket.
 * Guarantees zero-knowledge operation:
 *   • All outbound local Yjs CRDT updates are encrypted with AES-256-GCM ([12B IV][ciphertext])
 *     before transmission.
 *   • All inbound remote updates are decrypted with the Document Encryption Key (DEK)
 *     and applied to the local Y.Doc with origin 'remote'.
 *   • Drives client-side compaction when notified by the DO (`compaction_needed`).
 *   • Automatic reconnection with exponential backoff & jitter on disconnection.
 */

import * as Y from 'yjs'
import {
  acknowledgeYjsBatch,
  assignYjsBatch,
  clearEmergencyYjsState,
  loadLocalYjsOutbox,
  persistLocalYjsCache,
  persistLocalYjsUpdate,
  restoreEmergencyYjsState,
  restoreLocalYjsState,
  writeEmergencyYjsState,
  type LocalYjsDocument,
} from '../pages/yjsLocalPersistence'
import type { StoredYjsOutboxEntry, YjsServerCursor } from '../platform/webStorage'

export type ConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'error' | 'epoch_mismatch'
export type DeliveryStatus = 'synced' | 'saved_local' | 'syncing' | 'saving_local' | 'local_error'

export interface EncryptedYjsProviderOptions {
  ydoc: Y.Doc
  docId: string
  docKey: CryptoKey // Document Encryption Key (DEK)
  wsUrl: string
  authToken?: string | (() => string | Promise<string | null> | null)
  /**
   * If provided, the provider will POST to this URL (with the access token in the
   * Authorization header) to exchange it for a single-use WebSocket ticket before
   * each connection attempt. The ticket is placed in the WebSocket URL instead of
   * the raw JWT, keeping the JWT out of server access logs and browser history.
   *
   * Set to the ticket endpoint: `/api/documents/:id/ws-ticket`. Tickets are
   * single-use and expire after 30 seconds.
   */
  ticketUrl?: string
  epoch?: number
  onStatusChange?: (status: ConnectionStatus) => void
  onDeliveryStatusChange?: (status: DeliveryStatus) => void
  /** The server stored a batch of this page's local edits. */
  onBatchAcknowledged?: (updateCount: number) => void
  onKeyRefreshNeeded?: () => Promise<CryptoKey | null>
  /** Refreshes an expired access JWT before retrying the WebSocket ticket request once. */
  onAuthRefreshNeeded?: () => Promise<string | null>
  isNew?: boolean
  /** Local state restored before the editor was bound; avoids repeating page-open I/O. */
  recoveredLocalUpdates?: Array<{ entry: StoredYjsOutboxEntry; update: Uint8Array }>
  localStateRestored?: boolean
  localDocument?: LocalYjsDocument
}

export class EncryptedYjsProvider {
  private static readonly MAX_FAILED_PACKET_BUFFER = 50
  private static readonly IDLE_FLUSH_MS = 800
  private static readonly MAX_FLUSH_MS = 2_000
  private static readonly MAX_BATCH_UPDATES = 50
  private static readonly MAX_BATCH_BYTES = 64 * 1024
  private static readonly ACK_TIMEOUT_MS = 5_000
  private ydoc: Y.Doc
  private docId: string
  private docKey: CryptoKey
  private ws: WebSocket | null = null
  private wsUrl: string
  private authToken?: string | (() => string | Promise<string | null> | null)
  private ticketUrl?: string
  private epoch: number = 1
  private destroyed: boolean = false
  private closing: boolean = false
  private retryCount: number = 0
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private currentStatus: ConnectionStatus = 'disconnected'
  private currentDeliveryStatus: DeliveryStatus = 'synced'
  private onStatusChange?: (status: ConnectionStatus) => void
  private onDeliveryStatusChange?: (status: DeliveryStatus) => void
  private onBatchAcknowledged?: (updateCount: number) => void
  private updateHandler: (update: Uint8Array, origin: any) => void
  private isNew: boolean = false
  private hasLoggedDecryptionFailure: boolean = false
  private isCompacting: boolean = false
  private lastCompactedSeq: number = 0
  private pendingCompactionTimer: ReturnType<typeof setTimeout> | null = null
  private pendingCompactionSeq: number = 0
  private onKeyRefreshNeeded?: () => Promise<CryptoKey | null>
  private onAuthRefreshNeeded?: () => Promise<string | null>
  private isRefreshingKey: boolean = false
  private failedPacketBuffer: Uint8Array[] = []
  private pendingUpdates: Array<{ id: string; update: Uint8Array; batchId?: string }> = []
  private emergencyPending: Array<{ id: string; update: Uint8Array }> = []
  private persistenceChain: Promise<void> = Promise.resolve()
  private idleFlushTimer: ReturnType<typeof setTimeout> | null = null
  private maxFlushTimer: ReturnType<typeof setTimeout> | null = null
  private ackTimer: ReturnType<typeof setTimeout> | null = null
  private inFlightBatch: { batchId: string; entryIds: string[] } | null = null
  private hasAdvertisedState = false
  private mustAdvertiseState = false
  /** Cursor for callers without a LocalYjsDocument; otherwise the document owns it. */
  private ownServerCursor: YjsServerCursor | null = null
  /** The stored cursor this connection asked the server to resume from. */
  private connectionSentCursor: YjsServerCursor | null = null
  /** Log id confirmed by this connection's catchup; null until then. */
  private connectionLogId: string | null = null
  /** A frame on this connection could not be applied, so the cursor must not move. */
  private connectionCursorTainted = false
  private recoveredLocalUpdates?: Array<{ entry: StoredYjsOutboxEntry; update: Uint8Array }>
  private localStateRestored = false
  private cachePersistTimer: ReturnType<typeof setTimeout> | null = null
  private cachePersistenceChain: Promise<void> = Promise.resolve()
  private localDocument?: LocalYjsDocument
  private unsubscribeLocalDocument?: () => void
  private inboundChain: Promise<void> = Promise.resolve()
  private catchupMarkerReceived = false
  private initialSyncComplete = false
  private resolveInitialSync!: () => void
  private initialSyncPromise: Promise<void>
  private pageHideHandler = () => { void this.flushPendingUpdates().catch(() => {}) }
  private visibilityHandler = () => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      void this.flushPendingUpdates().catch(() => {})
    }
  }

  constructor(options: EncryptedYjsProviderOptions) {
    this.initialSyncPromise = new Promise((resolve) => {
      this.resolveInitialSync = resolve
    })
    this.ydoc = options.ydoc
    this.docId = options.docId
    this.docKey = options.docKey
    this.wsUrl = options.wsUrl
    this.authToken = options.authToken
    this.ticketUrl = options.ticketUrl
    this.epoch = options.epoch ?? 1
    this.onStatusChange = options.onStatusChange
    this.onDeliveryStatusChange = options.onDeliveryStatusChange
    this.onBatchAcknowledged = options.onBatchAcknowledged
    this.isNew = options.isNew ?? false
    this.onKeyRefreshNeeded = options.onKeyRefreshNeeded
    this.onAuthRefreshNeeded = options.onAuthRefreshNeeded
    this.recoveredLocalUpdates = options.recoveredLocalUpdates
    this.localStateRestored = options.localStateRestored ?? false
    this.localDocument = options.localDocument

    // Persist every local update first; cloud delivery is deliberately batched.
    // Remote updates also refresh the compact local cache so the next launch is
    // complete even when the user made no subsequent local edit.
    this.updateHandler = (update: Uint8Array, origin: any) => {
      if (origin !== 'local-cache') this.scheduleLocalCachePersistence()
      if (origin === 'remote' || origin === 'local-cache' || this.destroyed || this.closing) return
      this.queueLocalUpdate(new Uint8Array(update))
    }

    if (this.localDocument) {
      this.pendingUpdates = this.localDocument.pendingUpdates
      this.unsubscribeLocalDocument = this.localDocument.subscribe(() => {
        this.setDeliveryStatus(this.localDocument!.localSaveStatus)
        if (this.localDocument!.localSaveStatus === 'saved_local') this.scheduleFlush()
      })
    } else {
      this.ydoc.on('update', this.updateHandler)
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('pagehide', this.pageHideHandler)
      document.addEventListener('visibilitychange', this.visibilityHandler)
    }
    void this.initialize()
  }

  private setStatus(status: ConnectionStatus) {
    this.currentStatus = status
    this.onStatusChange?.(status)
  }

  private setDeliveryStatus(status: DeliveryStatus) {
    this.currentDeliveryStatus = status
    this.onDeliveryStatusChange?.(status)
  }

  private async initialize(): Promise<void> {
    try {
      let stored = this.recoveredLocalUpdates
      if (this.localDocument) {
        stored = []
      } else if (!this.localStateRestored) {
        // Compatibility path for callers that have not preloaded the local replica.
        // Emergency-journal edits are not in the outbox, so the full state must
        // be sent once to deliver them.
        if (restoreEmergencyYjsState(this.docId, this.ydoc)) this.mustAdvertiseState = true
        await restoreLocalYjsState(this.docId, this.ydoc, this.docKey)
        stored = await loadLocalYjsOutbox(this.docId, this.docKey)
        for (const { update } of stored) {
          Y.applyUpdate(this.ydoc, update, 'local-cache')
        }
      }
      stored ||= []
      if (!this.localDocument) {
        const existingIds = new Set(this.pendingUpdates.map((item) => item.id))
        const recovered = stored
          .filter(({ entry }) => !existingIds.has(entry.id))
          .map(({ entry, update }) => ({ id: entry.id, update, batchId: entry.batchId }))
        this.pendingUpdates = [...recovered, ...this.pendingUpdates]
      }
      if (this.pendingUpdates.length > 0) this.setDeliveryStatus(this.localDocument?.localSaveStatus ?? 'saved_local')
    } catch (err) {
      console.warn(`[EncryptedYjsProvider] Local recovery failed for ${this.docId}:`, err)
    } finally {
      this.scheduleLocalCachePersistence()
      await this.connect()
    }
  }

  private scheduleLocalCachePersistence(): void {
    if (this.destroyed || this.closing) return
    if (this.localDocument) {
      this.localDocument.scheduleCachePersistence()
      return
    }
    if (this.cachePersistTimer) clearTimeout(this.cachePersistTimer)
    this.cachePersistTimer = setTimeout(() => {
      this.cachePersistTimer = null
      const snapshot = Y.encodeStateAsUpdate(this.ydoc)
      const cursor = this.ownServerCursor
      this.persistOwnCache(snapshot, cursor)
    }, 500)
  }

  private persistOwnCache(snapshot: Uint8Array, cursor: YjsServerCursor | null): void {
    const key = this.docKey
    const retry = async () => {
      const task = this.cachePersistenceChain.catch(() => {}).then(async () => {
        await persistLocalYjsCache(this.docId, snapshot, key, cursor)
        localSaveSucceeded(`provider-cache:${this.docId}`)
      })
      this.cachePersistenceChain = task.catch(cause => localSaveFailed(`provider-cache:${this.docId}`, cause, retry))
      await task
    }
    void retry().catch(() => {})
  }

  private queueLocalUpdate(update: Uint8Array): void {
    const id = crypto.randomUUID()
    this.pendingUpdates.push({ id, update })

    // Keep the not-yet-committed updates in a synchronous emergency journal.
    // This closes the tiny window between the editor transaction and IndexedDB.
    this.emergencyPending.push({ id, update })
    writeEmergencyYjsState(
      this.docId,
      id,
      Y.mergeUpdates(this.emergencyPending.map((item) => item.update)),
    )
    this.setDeliveryStatus('saving_local')
    this.persistenceChain = this.persistenceChain.catch(() => {}).then(() => this.persistOwnPending()).catch(() => {})

    this.scheduleFlush()
  }

  private async persistOwnPending(): Promise<void> {
    try {
      const latest = this.emergencyPending[this.emergencyPending.length - 1]
      for (const item of [...this.emergencyPending]) {
        await persistLocalYjsUpdate(this.docId, item.id, item.update, this.docKey, this.epoch)
        this.emergencyPending = this.emergencyPending.filter(pending => pending.id !== item.id)
      }
      if (this.emergencyPending.length === 0 && latest) clearEmergencyYjsState(this.docId, latest.id)
      localSaveSucceeded(`provider:${this.docId}`)
      this.setDeliveryStatus(this.emergencyPending.length ? 'saving_local' : 'saved_local')
    } catch (cause) {
      localSaveFailed(`provider:${this.docId}`, cause, () => this.persistOwnPending())
      this.setDeliveryStatus('local_error')
      throw cause
    }
  }

  private scheduleFlush(): void {
    if (this.idleFlushTimer) clearTimeout(this.idleFlushTimer)
    this.idleFlushTimer = setTimeout(() => {
      this.idleFlushTimer = null
      void this.flushPendingUpdates().catch(() => {})
    }, EncryptedYjsProvider.IDLE_FLUSH_MS)

    if (!this.maxFlushTimer) {
      this.maxFlushTimer = setTimeout(() => {
        this.maxFlushTimer = null
        void this.flushPendingUpdates().catch(() => {})
      }, EncryptedYjsProvider.MAX_FLUSH_MS)
    }

    if (this.pendingUpdates.length >= EncryptedYjsProvider.MAX_BATCH_UPDATES) {
      void this.flushPendingUpdates().catch(() => {})
      return
    }
    const bytes = this.pendingUpdates.reduce((sum, item) => sum + item.update.byteLength, 0)
    if (bytes >= EncryptedYjsProvider.MAX_BATCH_BYTES) {
      void this.flushPendingUpdates().catch(() => {})
    }
  }

  private clearFlushTimers(): void {
    if (this.idleFlushTimer) clearTimeout(this.idleFlushTimer)
    if (this.maxFlushTimer) clearTimeout(this.maxFlushTimer)
    this.idleFlushTimer = null
    this.maxFlushTimer = null
  }

  /** Persisted, acknowledgment-based flush. At most one batch is in flight. */
  public async flushPendingUpdates(): Promise<void> {
    this.clearFlushTimers()
    if (
      this.destroyed ||
      this.inFlightBatch ||
      !this.ws ||
      this.ws.readyState !== WebSocket.OPEN ||
      this.pendingUpdates.length === 0
    ) return

    if (this.localDocument) await this.localDocument.whenPersisted()
    else { await this.persistenceChain; if (this.emergencyPending.length) await this.persistOwnPending() }
    if (this.destroyed || !this.ws || this.ws.readyState !== WebSocket.OPEN) return

    const first = this.pendingUpdates[0]
    const selected: typeof this.pendingUpdates = []
    let totalBytes = 0
    for (const item of this.pendingUpdates) {
      if (first.batchId && item.batchId !== first.batchId) break
      if (!first.batchId && item.batchId) break
      if (selected.length >= EncryptedYjsProvider.MAX_BATCH_UPDATES) break
      if (selected.length > 0 && totalBytes + item.update.byteLength > EncryptedYjsProvider.MAX_BATCH_BYTES) break
      selected.push(item)
      totalBytes += item.update.byteLength
    }
    if (selected.length === 0) return

    const batchId = first.batchId || crypto.randomUUID()
    const entryIds = selected.map((item) => item.id)
    if (!first.batchId) {
      if (this.localDocument) await this.localDocument.assignBatch(entryIds, batchId)
      else {
        await assignYjsBatch(entryIds, batchId)
        for (const item of selected) item.batchId = batchId
      }
    }

    try {
      // Without a LocalYjsDocument, acknowledgment deletes the outbox rows at
      // once, so the cache must contain the batch before it can be acknowledged.
      // A LocalYjsDocument keeps acknowledged rows until its own cache write.
      if (!this.localDocument) {
        await persistLocalYjsCache(this.docId, Y.encodeStateAsUpdate(this.ydoc), this.docKey, this.ownServerCursor)
      }
      const merged = Y.mergeUpdates(selected.map((item) => item.update))
      const packet = await this.encryptUpdate(merged)
      if (this.destroyed || !this.ws || this.ws.readyState !== WebSocket.OPEN) return

      // WebSocket frames are ordered. The metadata applies to the next binary
      // frame from this socket and is not broadcast to collaborators.
      this.ws.send(JSON.stringify({ type: 'update_batch', batchId }))
      this.ws.send(packet.buffer)
      this.inFlightBatch = { batchId, entryIds }
      this.setDeliveryStatus('syncing')
      this.ackTimer = setTimeout(() => {
        this.ackTimer = null
        this.inFlightBatch = null
        this.setDeliveryStatus('saved_local')
        void this.flushPendingUpdates().catch(() => {})
      }, EncryptedYjsProvider.ACK_TIMEOUT_MS)
    } catch (err) {
      console.error('[EncryptedYjsProvider] Failed to send update batch:', err)
      this.setDeliveryStatus('saved_local')
      if (!this.destroyed && !this.closing) {
        this.idleFlushTimer = setTimeout(() => {
          this.idleFlushTimer = null
          void this.flushPendingUpdates().catch(() => {})
        }, 1_000)
      }
    }
  }

  private async handleBatchAcknowledgment(batchId: string): Promise<void> {
    if (!this.inFlightBatch || this.inFlightBatch.batchId !== batchId) return
    if (this.ackTimer) clearTimeout(this.ackTimer)
    this.ackTimer = null
    const acknowledgedIds = new Set(this.inFlightBatch.entryIds)
    this.inFlightBatch = null
    this.onBatchAcknowledged?.(acknowledgedIds.size)
    try {
      if (this.localDocument) await this.localDocument.acknowledgeBatch(batchId)
      else {
        this.pendingUpdates = this.pendingUpdates.filter((item) => !acknowledgedIds.has(item.id))
        await acknowledgeYjsBatch(batchId)
      }
    } catch (err) {
      // The full local Yjs cache still protects the document. A stale outbox
      // row may be resent after restart, which Yjs and server dedup tolerate.
      console.warn('[EncryptedYjsProvider] Failed to prune acknowledged local batch:', err)
    }
    if (this.pendingUpdates.length > 0) {
      this.setDeliveryStatus('saved_local')
      void this.flushPendingUpdates().catch(() => {})
    } else {
      this.setDeliveryStatus('synced')
    }
  }

  private async encryptUpdate(update: Uint8Array): Promise<Uint8Array<ArrayBuffer>> {
    const iv = crypto.getRandomValues(new Uint8Array(12))
    const ciphertext = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: iv as BufferSource },
      this.docKey,
      update as BufferSource,
    )
    const packet = new Uint8Array(12 + ciphertext.byteLength)
    packet.set(iv, 0)
    packet.set(new Uint8Array(ciphertext), 12)
    return packet
  }

  /**
   * Fetch a single-use WebSocket ticket from the server.
   * The JWT is sent via the Authorization header (never in a URL).
   * Returns the opaque ticket string, or null if unavailable.
   */
  private async fetchWsTicket(token: string, allowAuthRetry = true): Promise<string | null> {
    if (!this.ticketUrl) return null
    try {
      return await withRequestDeadline(async signal => {
        const res = await fetch(this.ticketUrl!, {
          signal,
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        })
        if (res.status === 401 && allowAuthRetry && this.onAuthRefreshNeeded) {
          const refreshedToken = await this.onAuthRefreshNeeded()
          throwIfAborted(signal)
          if (refreshedToken) {
            return this.fetchWsTicket(refreshedToken, false)
          }
        }
        if (!res.ok) {
          console.warn('[EncryptedYjsProvider] Failed to fetch WebSocket ticket:', res.status)
          return null
        }
        const { ticket } = await res.json() as { ticket: string }
        throwIfAborted(signal)
        return this.destroyed || this.closing ? null : ticket ?? null
      })
    } catch (err) {
      console.warn('[EncryptedYjsProvider] WebSocket ticket fetch error:', err)
      return null
    }
  }


  public async connect(): Promise<void> {
    if (this.destroyed || this.closing) return
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }

    try {
      this.setStatus('connecting')

      // Resolve token
      let token: string | null = null
      if (typeof this.authToken === 'function') {
        token = await this.authToken()
      } else if (typeof this.authToken === 'string') {
        token = this.authToken
      }

      const url = new URL(this.wsUrl)

      if (token && this.ticketUrl) {
        // Preferred path: exchange JWT for a single-use ticket so the JWT never
        // appears in the WebSocket URL (and therefore never in access/edge logs).
        const ticket = await this.fetchWsTicket(token)

        // The ticket fetch is a real network round-trip. If the caller switched
        // documents (destroy()) while we were awaiting it, this provider is
        // already dead — opening a WebSocket now would create a live socket
        // nobody is managing, which then gets killed mid-handshake by whatever
        // destroys it next. Bail out before creating anything.
        if (this.destroyed || this.closing) return

        // Without a ticket, retry later rather than putting the JWT in the URL,
        // which is exactly what tickets exist to avoid.
        if (!ticket) {
          this.setStatus('error')
          this.scheduleReconnect()
          return
        }
        url.searchParams.set('ticket', ticket)
      } else if (token) {
        // No ticketUrl configured — legacy/non-browser path, put token directly in URL.
        url.searchParams.set('token', token)
      }

      if (this.destroyed || this.closing) return

      // Resume from the last cloud position this device has applied, so the
      // server only sends what is new. The server ignores a cursor that does
      // not belong to its current log and replies with a full catchup.
      this.connectionLogId = null
      this.connectionCursorTainted = false
      const cursor = this.serverCursor
      this.connectionSentCursor = cursor
      if (cursor) {
        url.searchParams.set('since', String(cursor.seq))
        url.searchParams.set('logId', cursor.logId)
      }

        this.ws = new WebSocket(url.toString())
      this.ws.binaryType = 'arraybuffer'

      this.ws.onopen = () => {
        if (this.destroyed || this.closing) {
          this.ws?.close()
          return
        }
        this.retryCount = 0
        this.setStatus('connected')
        this.hasLoggedDecryptionFailure = false

        if (this.isNew && this.ws) {
          try {
            this.ws.send(JSON.stringify({ type: 'clear_log' }))
            this.isNew = false
          } catch {}
        }

        if (this.pendingUpdates.length > 0) {
          void this.flushPendingUpdates().catch(() => {})
        }
      }

      this.ws.onmessage = (event: MessageEvent) => {
        this.inboundChain = this.inboundChain
          .then(() => this.handleWebSocketMessage(event))
          .catch((err) => {
            console.warn('[EncryptedYjsProvider] Failed to process WebSocket message:', err)
          })
      }

      this.ws.onerror = (err) => {
        console.warn('[EncryptedYjsProvider] WebSocket error:', err)
        this.setStatus('error')
      }

      this.ws.onclose = () => {
        if (!this.destroyed) {
          if (this.ackTimer) clearTimeout(this.ackTimer)
          this.ackTimer = null
          this.inFlightBatch = null
          if (this.pendingUpdates.length > 0) this.setDeliveryStatus(this.localDocument?.localSaveStatus ?? 'saved_local')
          this.setStatus('disconnected')
          this.scheduleReconnect()
        }
      }
    } catch (err) {
      console.error('[EncryptedYjsProvider] Failed to connect WebSocket:', err)
      this.setStatus('error')
      this.scheduleReconnect()
    }
  }

  private async handleWebSocketMessage(event: MessageEvent): Promise<void> {
    if (this.destroyed) return

    if (typeof event.data === 'string') {
      try {
        const msg = JSON.parse(event.data)
        if (msg.type === 'catchup_complete') {
          this.handleCatchupComplete(msg)
          this.catchupMarkerReceived = true
          this.completeInitialSyncIfReady()
        } else if (msg.type === 'log_reset') {
          this.handleLogReset(msg.logId)
        } else if (msg.type === 'compaction_needed') {
          // Sent after every row up to currentSeq was relayed to this socket.
          this.advanceServerCursor(msg.currentSeq)
          this.handleCompactionNeeded(msg.currentSeq, msg.urgent)
        } else if (msg.type === 'compaction_completed') {
          this.advanceServerCursor(msg.baseSeq)
          this.handleCompactionCompleted(msg.baseSeq)
        } else if (msg.type === 'update_ack' && typeof msg.batchId === 'string') {
          // The server relays each row before acknowledging any later one, so
          // everything up to serverSeq has already been applied here.
          this.advanceServerCursor(msg.serverSeq)
          await this.handleBatchAcknowledgment(msg.batchId)
        } else if (msg.type === 'error') {
          if (msg.code === 'log_capacity_reached') {
            console.warn('[EncryptedYjsProvider] Server log capacity reached, forcing immediate compaction')
            this.performCompaction(this.pendingCompactionSeq || this.lastCompactedSeq + 1)
          } else if (msg.code === 'rate_limited') {
            console.warn('[EncryptedYjsProvider] Client rate limited by server:', msg.message)
          } else if (msg.code === 'payload_too_large') {
            console.error('[EncryptedYjsProvider] Message rejected by server (payload too large):', msg.message)
          }
        }
      } catch (e) {
        console.warn('[EncryptedYjsProvider] Failed to parse text message:', e)
      }
      return
    }

    const data = new Uint8Array(event.data as ArrayBuffer)
    if (data.byteLength <= 12) {
      console.warn('[EncryptedYjsProvider] Received packet too small for IV')
      return
    }

    const iv = data.slice(0, 12)
    const ciphertext = data.slice(12)

    let decrypted: ArrayBuffer
    try {
      decrypted = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: iv as BufferSource },
        this.docKey,
        ciphertext as BufferSource
      )
    } catch (err) {
      await this.handleDecryptionFailure(data, err)
      return
    }

    // A throw here comes from an observer of the Y.Doc (such as the editor
    // binding), not from the key: Yjs has already merged the update, so it
    // is neither a key problem nor worth retrying.
    try {
      Y.applyUpdate(this.ydoc, new Uint8Array(decrypted), 'remote')
    } catch (err) {
      console.error('[EncryptedYjsProvider] A document observer failed while applying a remote update:', err)
    }
    if (this.currentStatus === 'epoch_mismatch') {
      this.setStatus('connected')
      this.hasLoggedDecryptionFailure = false
      this.completeInitialSyncIfReady()
    }
  }

  private get serverCursor(): YjsServerCursor | null {
    return this.localDocument ? this.localDocument.serverCursor : this.ownServerCursor
  }

  /**
   * The cursor is written with the next cache write (every network batch,
   * pagehide and close); `persist` forces one now.
   */
  private setServerCursor(cursor: YjsServerCursor, persist = false): void {
    if (this.localDocument) this.localDocument.setServerCursor(cursor)
    else this.ownServerCursor = { ...cursor }
    if (persist) this.scheduleLocalCachePersistence()
  }

  /**
   * Inbound frames are processed strictly in order, so by the time the catchup
   * marker is handled every catchup frame before it has been applied — unless
   * one failed to decrypt, in which case the stored cursor is left alone and the
   * next connection downloads those updates again.
   */
  private handleCatchupComplete(msg: { lastSeq?: unknown; logId?: unknown }): void {
    if (
      !this.connectionCursorTainted &&
      this.failedPacketBuffer.length === 0 &&
      typeof msg.logId === 'string' &&
      typeof msg.lastSeq === 'number' &&
      Number.isFinite(msg.lastSeq)
    ) {
      this.connectionLogId = msg.logId
      this.setServerCursor({ logId: msg.logId, seq: msg.lastSeq }, true)
    }

    // If this device has synced with this same cloud log before, the cloud
    // already holds everything it had, and the durable outbox covers the rest.
    // Otherwise (a page that never synced here, or a cloud log that was reset)
    // send the full local state once. A page with queued edits and no cursor
    // is new: the outbox already holds all of its content.
    const knownLog = this.connectionSentCursor !== null && this.connectionSentCursor.logId === msg.logId
    const needsFullState = this.mustAdvertiseState || (
      !knownLog && (this.pendingUpdates.length === 0 || this.connectionSentCursor !== null)
    )
    if (needsFullState && !this.hasAdvertisedState) {
      try {
        const currentState = Y.encodeStateAsUpdate(this.ydoc)
        if (currentState.byteLength > 2) {
          void this.encryptAndSend(currentState).then((sent) => {
            if (sent) this.hasAdvertisedState = true
          })
        }
      } catch (err) {
        console.error('[EncryptedYjsProvider] Failed to send initial local state:', err)
      }
    }
  }

  /** The server emptied its log for this new page; start counting from zero. */
  private handleLogReset(logId: unknown): void {
    if (typeof logId !== 'string' || this.connectionCursorTainted) return
    this.connectionLogId = logId
    this.setServerCursor({ logId, seq: 0 }, true)
  }

  private advanceServerCursor(seq: unknown): void {
    if (
      !this.connectionLogId ||
      this.connectionCursorTainted ||
      this.failedPacketBuffer.length > 0 ||
      typeof seq !== 'number' ||
      !Number.isFinite(seq)
    ) return
    const current = this.serverCursor
    if (current && current.logId === this.connectionLogId && seq <= current.seq) return
    this.setServerCursor({ logId: this.connectionLogId, seq })
  }

  public waitForInitialSync(): Promise<void> {
    return this.initialSyncPromise
  }

  private completeInitialSyncIfReady(): void {
    if (
      this.initialSyncComplete ||
      !this.catchupMarkerReceived ||
      this.currentStatus === 'epoch_mismatch'
    ) return
    this.initialSyncComplete = true
    this.resolveInitialSync()
  }

  private scheduleReconnect(): void {
    if (this.destroyed || this.closing || this.reconnectTimer) return
    const delay = Math.min(1000 * Math.pow(1.5, this.retryCount) + Math.random() * 500, 30000)
    this.retryCount++
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      this.connect()
    }, delay)
  }

  private async encryptAndSend(update: Uint8Array): Promise<boolean> {
    if (this.destroyed) return false
    const packet = await this.encryptUpdate(update)
    if (this.destroyed || !this.ws || this.ws.readyState !== WebSocket.OPEN) return false
    this.ws.send(packet.buffer)
    return true
  }

  public get isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN
  }

  public get isDestroyed(): boolean {
    return this.destroyed || this.closing
  }

  public get status(): ConnectionStatus {
    return this.currentStatus
  }

  /** Completion means server acknowledgment, never merely a socket send. */
  public async waitForDelivery(signal: AbortSignal): Promise<void> {
    await this.flushPendingUpdates()
    while (this.deliveryStatus !== 'synced') {
      if (signal.aborted) throw signal.reason
      if (this.isDestroyed) throw new Error('Page connection closed before acknowledgment')
      if (this.status === 'epoch_mismatch') throw new Error('The cloud document key changed; local edits are retained')
      await new Promise<void>((resolve, reject) => {
        const abort = () => { clearTimeout(timer); reject(signal.reason) }
        const timer = setTimeout(() => { signal.removeEventListener('abort', abort); resolve() }, 100)
        signal.addEventListener('abort', abort, { once: true })
      })
    }
  }

  public get deliveryStatus(): DeliveryStatus {
    return this.currentDeliveryStatus
  }

  public setDocKey(key: CryptoKey, epoch?: number): void {
    this.docKey = key
    if (epoch !== undefined) this.epoch = epoch
    this.localDocument?.setKey(key, epoch)
  }

  /**
   * Schedules compaction with randomized jitter to prevent thundering-herd issues
   * across multiple connected clients. Active foreground clients compact sooner
   * than backgrounded clients. When marked urgent, compacts immediately without jitter.
   */
  private handleCompactionNeeded(currentSeq: number, urgent?: boolean): void {
    if (typeof currentSeq !== 'number' || currentSeq <= this.lastCompactedSeq) {
      return
    }
    if (this.isCompacting) {
      return
    }

    if (urgent) {
      if (this.pendingCompactionTimer) {
        clearTimeout(this.pendingCompactionTimer)
        this.pendingCompactionTimer = null
      }
      this.pendingCompactionSeq = currentSeq
      this.performCompaction(currentSeq)
      return
    }

    // Foreground clients get 50-300ms jitter; backgrounded/hidden clients get 1000-2000ms
    const isBackground = typeof document !== 'undefined' && document.hidden
    const jitter = isBackground
      ? 1000 + Math.random() * 1000
      : 50 + Math.random() * 250

    if (this.pendingCompactionTimer) {
      if (currentSeq > this.pendingCompactionSeq) {
        this.pendingCompactionSeq = currentSeq
      }
      return
    }

    this.pendingCompactionSeq = currentSeq
    this.pendingCompactionTimer = setTimeout(async () => {
      this.pendingCompactionTimer = null
      await this.performCompaction(this.pendingCompactionSeq)
    }, jitter)
  }

  /**
   * Called when another client (or this client) has successfully completed compaction.
   * Cancels any pending compaction timer that is covered by this baseSeq.
   */
  private handleCompactionCompleted(baseSeq: number): void {
    if (typeof baseSeq !== 'number') return
    if (baseSeq > this.lastCompactedSeq) {
      this.lastCompactedSeq = baseSeq
    }
    if (this.pendingCompactionTimer && this.pendingCompactionSeq <= baseSeq) {
      clearTimeout(this.pendingCompactionTimer)
      this.pendingCompactionTimer = null
    }
  }

  /**
   * Client-side compaction: exports full CRDT state as a single encrypted snapshot
   * and submits it to the Durable Object.
   */
  public async performCompaction(baseSeq: number): Promise<void> {
    if (this.destroyed || !this.ws || this.ws.readyState !== WebSocket.OPEN) return
    if (this.isCompacting || (typeof baseSeq === 'number' && baseSeq <= this.lastCompactedSeq)) return

    this.isCompacting = true
    try {
      // 1. Export entire CRDT state as a single compact update
      const snapshot = Y.encodeStateAsUpdate(this.ydoc)

      // 2. Encrypt the snapshot with DEK
      const iv = crypto.getRandomValues(new Uint8Array(12))
      const encrypted = await crypto.subtle.encrypt(
        { name: 'AES-GCM', iv: iv as BufferSource },
        this.docKey,
        snapshot as BufferSource
      )

      // 3. Pack: [12 bytes IV][N bytes ciphertext]
      const packedSnapshot = new Uint8Array(12 + encrypted.byteLength)
      packedSnapshot.set(iv, 0)
      packedSnapshot.set(new Uint8Array(encrypted), 12)

      // 4. Send the snapshot: a small metadata frame, then the encrypted bytes
      // as the next binary frame (frames are ordered), like update batches.
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'snapshot', baseSeq, epoch: this.epoch }))
        this.ws.send(packedSnapshot.buffer)
        this.lastCompactedSeq = Math.max(this.lastCompactedSeq, baseSeq)
      }
    } catch (err) {
      console.error('[EncryptedYjsProvider] Compaction failed:', err)
    } finally {
      this.isCompacting = false
    }
  }

  public async retryKeyRefresh(): Promise<void> {
    if (this.destroyed) return
    this.hasLoggedDecryptionFailure = false
    await this.triggerKeyRefresh()
  }

  private async triggerKeyRefresh(): Promise<void> {
    if (this.destroyed || !this.onKeyRefreshNeeded || this.isRefreshingKey) {
      return
    }

    this.isRefreshingKey = true
    try {
      console.warn('[EncryptedYjsProvider] Requesting updated document key from server...')
      const newKey = await this.onKeyRefreshNeeded()
      if (this.destroyed) return

      if (newKey) {
        this.setDocKey(newKey)
        const packetsToReplay = [...this.failedPacketBuffer]
        this.failedPacketBuffer = []

        let failedCount = 0
        for (const p of packetsToReplay) {
          if (p.byteLength <= 12) continue
          const iv = p.slice(0, 12)
          const ciphertext = p.slice(12)
          let decrypted: ArrayBuffer
          try {
            decrypted = await crypto.subtle.decrypt(
              { name: 'AES-GCM', iv: iv as BufferSource },
              this.docKey,
              ciphertext as BufferSource
            )
          } catch {
            failedCount++
            if (this.failedPacketBuffer.length < EncryptedYjsProvider.MAX_FAILED_PACKET_BUFFER) {
              this.failedPacketBuffer.push(p)
            }
            continue
          }
          try {
            Y.applyUpdate(this.ydoc, new Uint8Array(decrypted), 'remote')
          } catch (err) {
            console.error('[EncryptedYjsProvider] A document observer failed while applying a remote update:', err)
          }
        }

        if (failedCount === 0) {
          console.log('[EncryptedYjsProvider] Successfully recovered from epoch mismatch and applied updates')
          this.setStatus('connected')
          this.hasLoggedDecryptionFailure = false
          this.completeInitialSyncIfReady()
        } else {
          console.warn(`[EncryptedYjsProvider] New key still failed to decrypt ${failedCount} packets`)
          this.setStatus('epoch_mismatch')
        }
      }
    } catch (refreshErr) {
      console.error('[EncryptedYjsProvider] Key refresh failed:', refreshErr)
      this.setStatus('epoch_mismatch')
    } finally {
      this.isRefreshingKey = false
    }
  }

  private async handleDecryptionFailure(packet: Uint8Array, err: any): Promise<void> {
    if (this.destroyed) return

    if (!this.hasLoggedDecryptionFailure) {
      console.error('[EncryptedYjsProvider] Decryption failed — possible epoch mismatch or stale DEK:', err)
      this.hasLoggedDecryptionFailure = true
    }

    this.setStatus('epoch_mismatch')
    this.connectionCursorTainted = true

    if (packet.byteLength > 12 && this.failedPacketBuffer.length < EncryptedYjsProvider.MAX_FAILED_PACKET_BUFFER) {
      this.failedPacketBuffer.push(packet)
    }

    await this.triggerKeyRefresh()
  }

  public destroy(): void {
    if (this.destroyed || this.closing) return
    this.closing = true
    if (this.localDocument) this.unsubscribeLocalDocument?.()
    else this.ydoc.off('update', this.updateHandler)
    this.clearFlushTimers()
    if (this.localDocument) {
      void this.localDocument.persistCacheNow().catch(() => {})
    } else if (this.cachePersistTimer) {
      clearTimeout(this.cachePersistTimer)
      this.cachePersistTimer = null
      const snapshot = Y.encodeStateAsUpdate(this.ydoc)
      const cursor = this.ownServerCursor
      this.persistOwnCache(snapshot, cursor)
    }
    // Give a connected socket a short opportunity to deliver and acknowledge
    // the final batch. The durable local outbox remains authoritative if this
    // grace period expires or the application is terminated sooner.
    void (async () => {
      if (this.localDocument) await this.localDocument.whenPersisted()
      else await this.persistenceChain
      await this.flushPendingUpdates()
      if (this.inFlightBatch) {
        await new Promise((resolve) => setTimeout(resolve, 500))
      }
      this.finalizeDestroy()
    })().catch(() => { this.finalizeDestroy() })
  }

  private finalizeDestroy(): void {
    if (this.destroyed) return
    this.destroyed = true
    this.failedPacketBuffer = []
    this.isRefreshingKey = false
    this.setStatus('disconnected')
    if (this.pendingCompactionTimer) {
      clearTimeout(this.pendingCompactionTimer)
      this.pendingCompactionTimer = null
    }
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    if (this.ackTimer) {
      clearTimeout(this.ackTimer)
      this.ackTimer = null
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('pagehide', this.pageHideHandler)
      document.removeEventListener('visibilitychange', this.visibilityHandler)
    }
    if (this.ws) {
      try {
        this.ws.close()
      } catch {}
      this.ws = null
    }
  }
}
